import {
  Box,
  Checkbox,
  Collapse,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { ChevronSmallUpIcon } from "@/components/common/icons";
import {
  GREY,
  PRIMARY,
  TEXT_DISABLED,
  TEXT_PRIMARY_DARK,
} from "@/config/mui/colorPalette";

export enum CheckboxState {
  CHECKED = "checked",
  INDETERMINATE = "indeterminate",
  UNCHECKED = "unchecked",
}

type ExpandableSelectableSectionProps = {
  title: string;
  state: CheckboxState;
  expanded?: boolean;
  onChange: () => void;
  disabled?: boolean;
  children: ReactNode;
  tooltip?: string;
};

export default function ExpandableSelectableSection({
  title,
  state,
  expanded = false,
  onChange,
  disabled = false,
  children,
  tooltip,
}: ExpandableSelectableSectionProps) {
  const [isExpanded, setIsExpanded] = useState(expanded);

  const handleClick = () => {
    if (disabled) return;
    setIsExpanded(!isExpanded);
  };

  return (
    <Box>
      <Stack
        direction="row"
        role="button"
        tabIndex={0}
        onClick={handleClick}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: GREY[5],
          borderRadius: 2,
          p: 0.5,
          cursor: disabled ? "default" : "pointer",
          "&:hover": {
            bgcolor: disabled ? GREY[5] : GREY[10],
          },
          color: disabled ? TEXT_DISABLED : TEXT_PRIMARY_DARK,
        }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Tooltip
            title={tooltip}
            placement="top-start"
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "168px",
                  backgroundColor: "rgba(58, 58, 65, 0.95) !important",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(58, 58, 65, 0.95) !important",
                },
              },
            }}
          >
            <Box>
              <Checkbox
                checked={state === CheckboxState.CHECKED}
                indeterminate={state === CheckboxState.INDETERMINATE}
                sx={{ color: PRIMARY }}
                onChange={onChange}
                onClick={(e) => e.stopPropagation()}
                disabled={disabled}
              />
            </Box>
          </Tooltip>
          <Typography variant="subtitleDefault">{title}</Typography>
        </Stack>
        <ChevronSmallUpIcon
          color={GREY[50]}
          style={{
            transition: "transform 0.3s",
            transform: `rotateZ(${isExpanded ? "0deg" : "180deg"})`,
            marginRight: "8px",
          }}
        />
      </Stack>
      <Collapse in={isExpanded} timeout="auto">
        {children}
      </Collapse>
    </Box>
  );
}
